import { createSelector } from 'reselect';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { createGroupBy, getDimensionNamesByMetric } from '@utils/dimension';
import { createErrorCodeFilter } from '@utils/filter';
import baseSelector, {
  metricSelector,
  filterSelector,
  intervalPayloadSelector,
} from '@store/errorDiagnostics/base/selectors';
import commonSelector from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';
import dimensionListSelector from '@store/common/dimensionList/selectors';
import { getIntervalByGranularity } from '@utils/granularity';
import { granularity as getGranularity } from '@utils';
import { metricKeys } from '@pulse/metrics';
import { DATASET } from '@utils/constants';
import { SCOPE } from './types';

const selector = (state) => get(state, SCOPE);

export default selector;

export const dimensionsSelector = createSelector(
  dimensionListSelector,
  commonSelector,
  (dimensionListStore, commomStore) => {
    const { isFetching, response: dimensionList } = dimensionListStore;
    const metric = metricKeys.Attempts;
    const { features } = commomStore;
    const customerAccountType = features[featureNames.CUSTOMER_ACCOUNT_TYPE];
    if (isFetching || dimensionList.length === 0 || !metric) {
      return [];
    }
    return getDimensionNamesByMetric(dimensionList, metric, [], customerAccountType) || [];
  },
);

export const originalDimensionsSelector = createSelector(
  selector,
  (store) => store.core.dimensions,
);

export const payloadsSelector = createSelector(
  selector,
  intervalPayloadSelector,
  filterSelector,
  metricSelector,
  baseSelector,
  dimensionListSelector,
  commonSelector,
  (store, interval, filter, metric, baseStore, dimensionListStore, commonStore) => {
    const { dimensions } = store.core;
    const { currentErrorCode: errorCode } = baseStore;
    const copiedFilter = cloneDeep(filter);
    const { response: dimensionList } = dimensionListStore;

    if (!Array.isArray(copiedFilter.rule)) {
      copiedFilter.rule = [];
    }

    copiedFilter.rule.push([createErrorCodeFilter(metric, errorCode)]);
    return dimensions.map((dimension) => {
      const dimensionDetails =
        dimensionList.find((originDimension) => originDimension.name === dimension) || dimension;
      let groupBy = createGroupBy(dimensionDetails);
      if (groupBy && groupBy.includes('=')) {
        groupBy = groupBy.split('=').slice(1).join('=');
      }

      const granularity = getGranularity({
        from: interval.from,
        to: interval.to,
        minuteGranularity: true,
      });

      const { from, to } = getIntervalByGranularity(interval, granularity);

      const { features } = commonStore;
      const dimensionRowsLimit = features[featureNames.ENUMERATED_VALUES_PER_DIMENSION];

      const queries = [
        {
          type: 'group-by',
          dataset: DATASET,
          metrics: [metric],
          interval: `${from}/${to}`,
          granularity,
          filter: copiedFilter.rule,
          limit: dimensionRowsLimit,
          groupBy: [groupBy],
          orderBy: 'desc',
          sortBy: [
            {
              metric,
              order: 'desc',
            },
          ],
          options: {
            withTotals: true,
          },
        },
      ];
      return { queries };
    });
  },
);
